import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getReviewQueue } from '../api';
import { getTmDisplayName, resolveTmId } from '../utils/languages';
import { patchRequestWithDraftFields } from '../utils/draftState';
import RequestsTable from './RequestsTable';

const REVIEW_QUEUE_TM_FILTER_ID = 'review-queue-tm-filter';

function FilterIcon() {
  return (
    <svg
      className="review-queue-tm-filter__label-icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      className="review-queue-tm-filter__info-icon"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

/**
 * ReviewQueue — displays API-submitted translations awaiting linguist review.
 *
 * JSON requests appear as single rows (same as text). The leaf-level
 * editing happens inside RequestDetail when the linguist clicks Review.
 */
function ReviewQueue({
  onViewRequest,
  availableTMs = [],
  currentUserId = '',
  onLockStatusChange,
  onNotify,
  draftListPatchRef,
}) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedTm, setSelectedTm] = useState('');

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReviewQueue();
      setRequests(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch review queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (!draftListPatchRef) return undefined;
    draftListPatchRef.current = (reqId, draftFields) => {
      setRequests((prev) => prev.map((row) => (
        row.req_id === reqId ? patchRequestWithDraftFields(row, draftFields) : row
      )));
    };
    return () => {
      draftListPatchRef.current = null;
    };
  }, [draftListPatchRef]);

  // Enrich each request with a display-friendly reviewer string
  const enrichedRequests = useMemo(() => requests.map((r) => {
    const reviewers = r.assigned_reviewers || [];
    return {
      ...r,
      notes: reviewers.length > 0
        ? `Reviewer: ${reviewers.join(', ')}`
        : r.notes || '',
    };
  }), [requests]);

  // Derive distinct TMs present in the queue for the filter dropdown
  const tmOptions = useMemo(() => {
    const seen = new Map();
    for (const r of enrichedRequests) {
      const id = resolveTmId(r, availableTMs);
      if (id && !seen.has(id)) {
        seen.set(id, getTmDisplayName(id, availableTMs) || id);
      }
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ id, name }));
  }, [enrichedRequests, availableTMs]);

  // Client-side filter by TM
  const filteredRequests = useMemo(() => {
    if (!selectedTm) return enrichedRequests;
    return enrichedRequests.filter(
      (r) => resolveTmId(r, availableTMs) === selectedTm,
    );
  }, [enrichedRequests, selectedTm, availableTMs]);

  const tmFilter = (
    <div className="review-queue-tm-filter">
      <div className="review-queue-tm-filter__label-row">
        <label className="review-queue-tm-filter__label" htmlFor={REVIEW_QUEUE_TM_FILTER_ID}>
          <FilterIcon />
          Filter by Translation Memory
        </label>
        <span
          className="review-queue-tm-filter__info"
          title="Show only API translations that use the selected translation memory. Leave on All Translation Memories to see the full queue."
          aria-label="Show only API translations that use the selected translation memory. Leave on All Translation Memories to see the full queue."
        >
          <InfoIcon />
        </span>
      </div>
      <div className="review-queue-tm-filter__select-wrap">
        <select
          id={REVIEW_QUEUE_TM_FILTER_ID}
          className="form-select review-queue-tm-filter__select"
          value={selectedTm}
          onChange={(e) => setSelectedTm(e.target.value)}
        >
          <option value="">All Translation Memories</option>
          {tmOptions.map((tm) => (
            <option key={tm.id} value={tm.id}>{tm.name}</option>
          ))}
        </select>
        <span className="review-queue-tm-filter__chevron" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>
    </div>
  );

  return (
    <RequestsTable
      requests={filteredRequests}
      loading={loading}
      onRefresh={fetchQueue}
      onViewRequest={onViewRequest}
      lastRefresh={lastRefresh}
      title="Review Translations"
      subtitle="API-submitted translations awaiting linguist review"
      emptyMessage="No API translations pending review."
      emptyHint="Translations submitted via the API will appear here once processed."
      headerHelper={tmFilter}
      showActions
      availableTMs={availableTMs}
      currentUserId={currentUserId}
      onLockStatusChange={onLockStatusChange}
      onNotify={onNotify}
    />
  );
}

export default ReviewQueue;
